/**
 * Youth Banking Financial & Loan Calculation Engine
 * Gold Minds Portal
 */

export interface LoanCalculationResult {
  monthlyPayment: number;
  totalRepayment: number;
  totalInterest: number;
  effectiveMonthlyRate: number;
}

export interface AmortizationInstallment {
  installmentNumber: number;
  dueDate: string;
  amount: number;
  principal: number;
  interest: number;
  remainingBalance: number;
  status: 'pending' | 'paid' | 'overdue';
  paidAt?: string;
  transactionId?: string;
}

export interface LoanEligibilityResult {
  maxLoanAmount: number;
  maxTermMonths: number;
  recommendedInterestRate: number;
  isEligible: boolean;
  creditTier: 'Prime' | 'Standard' | 'Caution' | 'Restricted';
  meritDiscountApplied: boolean;
  activeLoansCount: number;
  reasons: string[];
}

export interface LoanProgressResult {
  totalAmount: number;
  paidAmount: number;
  remainingBalance: number;
  percentPaid: number;
  isPaidOff: boolean;
  isOverdue: boolean;
  nextInstallmentDueDate: string | null;
  nextInstallmentAmount: number;
}

/**
 * Grade-based borrowing maximums
 */
export const GRADE_BORROW_LIMITS: Record<string, { maxAmount: number; maxTerm: number; label: string }> = {
  elementary: { maxAmount: 50, maxTerm: 3, label: 'Grades 3–5 (Elementary)' },
  middle: { maxAmount: 150, maxTerm: 6, label: 'Grades 6–8 (Middle School)' },
  high: { maxAmount: 300, maxTerm: 12, label: 'Grades 9–12 (High School)' },
  default: { maxAmount: 100, maxTerm: 6, label: 'Standard Scholar' },
};

/**
 * Resolve grade category from grade string or number
 */
export function getGradeCategory(grade?: string | number | null): 'elementary' | 'middle' | 'high' | 'default' {
  if (grade === null || grade === undefined || grade === '') {
    return 'default';
  }

  const num = typeof grade === 'number' ? grade : parseInt(String(grade).replace(/[^0-9]/g, ''), 10);
  if (isNaN(num)) return 'default';

  if (num <= 5) return 'elementary';
  if (num <= 8) return 'middle';
  return 'high';
}

/**
 * Calculate loan monthly payment, total interest, and total repayment amount.
 * Handles 0% interest and edge cases without division by zero errors.
 */
export function calculateLoanRepayment(
  amount: number,
  termMonths: number,
  annualInterestRate: number
): LoanCalculationResult {
  const principal = Math.max(0, Number(amount) || 0);
  const term = Math.max(1, Math.round(Number(termMonths) || 1));
  const rate = Math.max(0, Number(annualInterestRate) || 0);

  if (principal === 0) {
    return {
      monthlyPayment: 0,
      totalRepayment: 0,
      totalInterest: 0,
      effectiveMonthlyRate: 0,
    };
  }

  // If 0% interest or invalid rate, simple linear amortization
  if (rate === 0) {
    const monthlyPayment = Math.round((principal / term) * 100) / 100;
    const totalRepayment = principal;
    return {
      monthlyPayment,
      totalRepayment,
      totalInterest: 0,
      effectiveMonthlyRate: 0,
    };
  }

  const monthlyRate = rate / 100 / 12;
  const factor = Math.pow(1 + monthlyRate, term);
  const emi = (principal * monthlyRate * factor) / (factor - 1);

  const monthlyPayment = Math.round(emi * 100) / 100;
  const totalRepayment = Math.round(monthlyPayment * term * 100) / 100;
  const totalInterest = Math.max(0, Math.round((totalRepayment - principal) * 100) / 100);

  return {
    monthlyPayment,
    totalRepayment,
    totalInterest,
    effectiveMonthlyRate: rate / 12,
  };
}

/**
 * Generate a complete amortization schedule breakdown with monthly installment dates
 */
export function generateAmortizationSchedule(
  amount: number,
  termMonths: number,
  annualInterestRate: number,
  startDate?: string | Date
): AmortizationInstallment[] {
  const principal = Math.max(0, Number(amount) || 0);
  const term = Math.max(1, Math.round(Number(termMonths) || 1));
  const rate = Math.max(0, Number(annualInterestRate) || 0);
  const start = startDate ? new Date(startDate) : new Date();

  const { monthlyPayment } = calculateLoanRepayment(principal, term, rate);
  const monthlyRate = rate / 100 / 12;

  let balance = principal;
  const schedule: AmortizationInstallment[] = [];

  for (let i = 1; i <= term; i += 1) {
    const dueDate = new Date(start);
    dueDate.setMonth(dueDate.getMonth() + i);

    let interestPart = Math.round(balance * monthlyRate * 100) / 100;
    let principalPart = Math.round((monthlyPayment - interestPart) * 100) / 100;

    // For the final installment, settle exact remaining principal
    if (i === term || principalPart > balance) {
      principalPart = balance;
    }

    balance = Math.max(0, Math.round((balance - principalPart) * 100) / 100);

    schedule.push({
      installmentNumber: i,
      dueDate: dueDate.toISOString(),
      amount: Math.round((principalPart + interestPart) * 100) / 100,
      principal: principalPart,
      interest: interestPart,
      remainingBalance: balance,
      status: 'pending',
    });
  }

  return schedule;
}

/**
 * Calculate student borrowing eligibility and merit-based interest rate
 */
export function calculateStudentLoanEligibility(
  grade?: string | number | null,
  streetCredScore = 0,
  checkingBalance = 0,
  activeLoansCount = 0
): LoanEligibilityResult {
  const gradeCategory = getGradeCategory(grade);
  const limits = GRADE_BORROW_LIMITS[gradeCategory];
  const reasons: string[] = [];

  let creditTier: LoanEligibilityResult['creditTier'] = 'Standard';
  let recommendedInterestRate = 5.0;
  let meritDiscountApplied = false;

  // Evaluate Street Cred merits
  if (streetCredScore >= 50) {
    creditTier = 'Prime';
    recommendedInterestRate = 3.0;
    meritDiscountApplied = true;
    reasons.push('High S.T.R.E.E.T. Cred unlocked the 3.0% Prime Borrower discount!');
  } else if (streetCredScore >= 0) {
    creditTier = 'Standard';
    recommendedInterestRate = 5.0;
    reasons.push('Standard 5.0% interest rate applied.');
  } else if (streetCredScore >= -20) {
    creditTier = 'Caution';
    recommendedInterestRate = 7.0;
    reasons.push('Negative S.T.R.E.E.T. Cred results in a caution rate of 7.0%.');
  } else {
    creditTier = 'Restricted';
    recommendedInterestRate = 8.0;
    reasons.push('Critical behavior demerits require educator manual clearance.');
  }

  // Active loan check
  let isEligible = true;
  if (activeLoansCount >= 2) {
    isEligible = false;
    reasons.push('Maximum 2 active loans allowed. Please pay off current obligations first.');
  } else if (activeLoansCount === 1) {
    reasons.push('You have 1 active loan. Additional borrowing requires teacher underwriting.');
  }

  return {
    maxLoanAmount: limits.maxAmount,
    maxTermMonths: limits.maxTerm,
    recommendedInterestRate,
    isEligible,
    creditTier,
    meritDiscountApplied,
    activeLoansCount,
    reasons,
  };
}

/**
 * Calculate repayment progress, remaining balance, and next installment
 */
export function calculateLoanProgress(loan: {
  amount: number;
  total_amount?: number | null;
  paid_amount?: number | null;
  status?: string | null;
  due_date?: string | null;
  monthly_payment?: number | null;
  extendedproperties?: any;
}): LoanProgressResult {
  const amount = Number(loan.amount) || 0;
  const totalAmount = Number(loan.total_amount) || amount;
  const paidAmount = Number(loan.paid_amount) || 0;
  const remainingBalance = Math.max(0, Math.round((totalAmount - paidAmount) * 100) / 100);

  const percentPaid = totalAmount > 0 ? Math.min(100, Math.round((paidAmount / totalAmount) * 100)) : 100;
  const isPaidOff = remainingBalance === 0 || loan.status === 'Paid';

  // Check overdue
  let isOverdue = loan.status === 'Overdue';
  if (!isPaidOff && loan.due_date) {
    const dueTime = new Date(loan.due_date).getTime();
    if (dueTime < Date.now()) {
      isOverdue = true;
    }
  }

  // Find next pending installment from amortization schedule if present
  let nextInstallmentDueDate: string | null = loan.due_date || null;
  let nextInstallmentAmount = Number(loan.monthly_payment) || remainingBalance;

  const schedule: AmortizationInstallment[] = loan.extendedproperties?.amortizationSchedule || [];
  if (schedule.length > 0) {
    const nextPending = schedule.find((item) => item.status === 'pending');
    if (nextPending) {
      nextInstallmentDueDate = nextPending.dueDate;
      nextInstallmentAmount = nextPending.amount;
    }
  }

  return {
    totalAmount,
    paidAmount,
    remainingBalance,
    percentPaid,
    isPaidOff,
    isOverdue,
    nextInstallmentDueDate,
    nextInstallmentAmount,
  };
}

/**
 * Format currency with Scholar Dollar symbol
 */
export function formatScholarDollars(amount: number | string | null | undefined): string {
  const num = Number(amount) || 0;
  return `$D ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

